/**
 * BL-2020 — the route-boundary check that an operator-named amount fits the LOT:
 * in the lot's currency, and no finer than that currency's minor unit (¥1, $0.01).
 * Shared by every admin door that places a bid at a typed amount; the step copy
 * itself lives in `money-step.ts`, shared with the schedule writes (BUG-1960).
 */
import { isSupportedCurrency, isWholeMinorUnits, type Money } from '@auctogon/core/money';

import { minorUnitStepMessage } from './money-step';

/** The refusal for a request whose amount names another currency than the lot's. */
export const LOT_AMOUNT_CURRENCY_MISMATCH_MESSAGE = "This amount is not in the lot's currency.";

/** Machine-readable codes for {@link lotAmountRefusal}. */
export const LOT_AMOUNT_ERROR_CODES = {
  CURRENCY_MISMATCH: 'CURRENCY_MISMATCH',
  AMOUNT_FINER_THAN_MINOR_UNIT: 'AMOUNT_FINER_THAN_MINOR_UNIT',
} as const;

const LOT_AMOUNT_CODES: ReadonlySet<string> = new Set(Object.values(LOT_AMOUNT_ERROR_CODES));

/**
 * Whether a create refusal's code is one of the money check's codes (BL-2131), so a door can echo it.
 *
 * @param code - a refusal's code, if it has one.
 * @returns `true` when it is a {@link LOT_AMOUNT_ERROR_CODES} value.
 */
export function isLotAmountErrorCode(code: string | undefined): code is string {
  return code != null && LOT_AMOUNT_CODES.has(code);
}

/** Why an amount does not fit its lot; `message` belongs under the amount field. */
export interface LotAmountRefusal {
  code: (typeof LOT_AMOUNT_ERROR_CODES)[keyof typeof LOT_AMOUNT_ERROR_CODES];
  message: string;
}

/**
 * Judge a decoded request amount against the lot it is placed on. The currency
 * is checked first: an amount's digits mean nothing in the wrong currency.
 *
 * @param input.amount - The request's amount, already decoded to Money.
 * @param input.lotCurrency - The lot row's own `currency`.
 * @returns The refusal, or `null` when the amount fits the lot.
 */
export function lotAmountRefusal(input: { amount: Money; lotCurrency: string }): LotAmountRefusal | null {
  const { amount, lotCurrency } = input;
  if (!isSupportedCurrency(lotCurrency) || amount.currency !== lotCurrency) {
    return { code: LOT_AMOUNT_ERROR_CODES.CURRENCY_MISMATCH, message: LOT_AMOUNT_CURRENCY_MISMATCH_MESSAGE };
  }
  if (!isWholeMinorUnits(amount)) {
    return {
      code: LOT_AMOUNT_ERROR_CODES.AMOUNT_FINER_THAN_MINOR_UNIT,
      message: minorUnitStepMessage(lotCurrency),
    };
  }
  return null;
}

/** A refusal tied to the request field it belongs under. */
export interface LotFieldAmountRefusal extends LotAmountRefusal {
  field: string;
}

/**
 * BL-2131 — the first decoded lot money field whose amount does not fit the
 * record it is written to, in the order the fields are given. Shared by the lot
 * PATCH (against the lot's own currency) and both create doors (against the
 * tenant currency the insert stamps), so every lot write door asks the same
 * question with the same copy. `null` / absent fields carry no amount and pass.
 *
 * @param input.amounts - The decoded money fields, keyed by request field.
 * @param input.lotCurrency - The currency the record carries (or will carry); null
 *   when it can't be resolved, which refuses every amount rather than guessing.
 * @returns The first refusal with its field, or `null` when every amount fits.
 */
export function firstLotAmountRefusal(input: {
  amounts: Record<string, Money | null | undefined>;
  lotCurrency: string | null;
}): LotFieldAmountRefusal | null {
  for (const [field, amount] of Object.entries(input.amounts)) {
    if (amount == null) continue;
    const refusal = lotAmountRefusal({ amount, lotCurrency: input.lotCurrency ?? '' });
    if (refusal) return { field, ...refusal };
  }
  return null;
}
